<template>
  <div class="page-container">
    <div v-if="initialLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
    </div>

    <div class="video-area" v-show="!initialLoading">
      <img
        v-if="visualState.type === 'image'"
        :src="visualState.src"
        class="hero-image"
        alt="virtual-human-cover"
      >
      <video
        v-else
        :key="videoRenderKey"
        ref="videoPlayer"
        :loop="visualState.loop"
        :muted="visualState.muted"
        preload="auto"
        playsinline
        webkit-playsinline
        x5-playsinline
        @ended="handleVideoEnded"
        @click="retryPendingVideoSoundPlayback"
      >
        <source :src="visualState.src" type="video/mp4">
      </video>
    </div>

    <div class="title-area">
      <div class="title-content">
        <h2>AI Services</h2>
        <div class="language-button" @click="toggleLanguageMenu">
          {{ selectedLanguage ? languageConfig[selectedLanguage] : '請選擇語系' }}
        </div>
      </div>
    </div>

    <div class="chat-container">
      <div class="messages-area" ref="messagesArea">
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message', message.type]"
        >
          <div class="message-content">
            <template v-if="message.isLoading">
              <span class="loading-dots"></span>
            </template>
            <template v-else>
              <template v-for="(segment, segmentIndex) in message.segments">
                <span
                  v-if="segment.type === 'text'"
                  :key="`text-${segmentIndex}`"
                  class="message-text"
                >
                  {{ segment.content }}
                </span>
                <a
                  v-else-if="segment.type === 'link'"
                  :key="`link-${segmentIndex}`"
                  :href="segment.content"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="message-link"
                >
                  {{ segment.label || segment.content }}
                </a>
                <a
                  v-else
                  :key="`image-${segmentIndex}`"
                  :href="segment.content"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="message-image-link"
                >
                  <img
                    :src="segment.content"
                    class="message-image"
                    :alt="segment.label || 'chat-image'"
                  >
                </a>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div v-if="showLanguageMenu" class="language-dropdown">
        <div
          v-for="(name, code) in languageConfig"
          :key="code"
          class="language-option"
          @click="selectLanguage(code)"
        >
          {{ name }}
        </div>
      </div>

      <div
        v-if="showLanguageMenu"
        class="overlay"
      ></div>

      <div v-if="currentFaqs.length" class="faq-area">
        <button
          v-for="(question, index) in currentFaqs"
          :key="`${selectedLanguage}-${index}`"
          class="faq-button"
          :disabled="isLoading"
          @click="sendPresetQuestion(question)"
        >
          {{ question }}
        </button>
      </div>

      <div v-if="currentFaqs.length" class="input-divider"></div>

      <div class="input-area">
        <input
          v-model="newMessage"
          @keyup.enter="sendMessage"
          type="text"
          :placeholder="inputPlaceholder"
          :disabled="isLoading || !selectedLanguage"
        >
        <button
          v-if="isMobileDevice"
          class="send-button mic-button"
          @touchstart.prevent="!isLoading && startVoiceInput()"
          @touchend.prevent="!isLoading && stopVoiceInput()"
          @touchcancel.prevent="!isLoading && stopVoiceInput()"
          @mousedown.prevent="!isLoading && startVoiceInput()"
          @mouseup.prevent="!isLoading && stopVoiceInput()"
          @mouseleave.prevent="!isLoading && stopVoiceInput()"
          @contextmenu.prevent
          :disabled="isLoading || !selectedLanguage"
          :class="{ listening: isListening, disabled: isLoading || !selectedLanguage }"
        >
          <audio-outlined :style="{ width: '20px', height: '20px' }" />
          <span v-if="isListening" class="mic-hint">放開 結束</span>
        </button>
        <button class="send-button" @click="sendMessage" :disabled="isLoading || !selectedLanguage">
          <loading-outlined v-if="isLoading" />
          <send-outlined v-else />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { api, handleApiError } from '@/api/api'
import { speechService, handleSpeechError } from '@/api/speech'
import { parseChatMessage, resolveChatMessageSegments } from '@/utils/chatMessageParser'
import { SendOutlined, LoadingOutlined, AudioOutlined } from '@ant-design/icons-vue'

const DEFAULT_WELCOME_MESSAGES = {
  'zh-TW': '您好，我是AI虛擬解說員，歡迎向我提問關於這個地方的任何問題~',
  'zh-CN': '您好，我是AI虚拟解说员，欢迎向我提问关于这个地方的任何问题~',
  'ja-JP': 'こんにちは、AIバーチャルガイドです。この場所について何でもお尋ねください~',
  'ko-KR': '안녕하세요, AI 가상 안내원입니다. 이 장소에 대해 궁금한 점을 물어보세요~',
  'th-TH': 'สวัสดี ฉันเป็นผู้บรรยายเสมือนจริง AI ยินดีตอบคำถามเกี่ยวกับสถานที่นี้~',
  'id-ID': 'Halo, saya adalah pemandu virtual AI. Silakan tanyakan apa saja tentang tempat ini~',
  'es-ES': 'Hola, soy un guia virtual con IA. Puedes preguntarme cualquier cosa sobre este lugar~',
  'en-US': 'Hello, I am an AI virtual guide. Feel free to ask me any questions about this place~',
  'de-DE': 'Hallo, ich bin ein virtueller KI-Guide. Fragen Sie mich gerne alles ueber diesen Ort~',
  'fr-FR': 'Bonjour, je suis votre guide virtuel IA. N\'hesitez pas a me poser des questions sur cet endroit~',
  'it-IT': 'Ciao, sono una guida virtuale AI. Sentiti libero di farmi domande su questo posto~',
}

const DEFAULT_PLACEHOLDERS = {
  'zh-TW': '請輸入訊息...',
  'zh-CN': '请输入讯息...',
}

const FALLBACK_OPENING_VIDEO = new URL('@/assets/startM.mp4', import.meta.url).href
const FALLBACK_REPLY_VIDEO = new URL('@/assets/replayM.mp4', import.meta.url).href

function createMessage(content, type, extra = {}) {
  const convertImageLinks = type === 'received'

  return {
    content,
    type,
    segments: parseChatMessage(content, { convertImageLinks }),
    ...extra,
  }
}

export default {
  name: 'ChatView',
  components: {
    SendOutlined,
    LoadingOutlined,
    AudioOutlined,
  },
  data() {
    return {
      messages: [],
      guid: null,
      newMessage: '',
      showLanguageMenu: true,
      languageConfig: {
        'zh-TW': '繁體中文',
        'zh-CN': '简体中文',
        'ja-JP': '日本語',
        'ko-KR': '한국어',
        'th-TH': 'ภาษาไทย',
        'id-ID': 'Bahasa Indonesia',
        'es-ES': 'Español',
        'en-US': 'English',
        'de-DE': 'Deutsch',
        'fr-FR': 'Français',
        'it-IT': 'Italiano',
      },
      selectedLanguage: '',
      param: '',
      agencyCode: null,
      knowledgeData: null,
      isLoading: false,
      initialLoading: true,
      isSpeaking: false,
      currentVisualMode: 'idle',
      siteConfig: {
        idleImageUrl: '',
        openingVideoUrl: '',
        replyVideoUrl: '',
        welcomeMessages: {},
        suggestedQuestions: [],
      },
      visualState: {
        type: 'video',
        src: FALLBACK_REPLY_VIDEO,
        loop: false,
        muted: true,
      },
      videoRenderKey: 0,
      isMobileDevice: false,
      recognition: null,
      isListening: false,
      pendingVideoSoundRetry: false,
    }
  },
  computed: {
    inputPlaceholder() {
      return DEFAULT_PLACEHOLDERS[this.selectedLanguage] || 'Enter your question...'
    },
    currentWelcomeMessage() {
      return this.siteConfig.welcomeMessages[this.selectedLanguage] || DEFAULT_WELCOME_MESSAGES[this.selectedLanguage] || DEFAULT_WELCOME_MESSAGES['zh-TW']
    },
    currentFaqs() {
      return (this.siteConfig.suggestedQuestions || [])
        .map(item => (item.translations && item.translations[this.selectedLanguage]) || '')
        .filter(Boolean)
    },
  },
  methods: {
    scrollToBottom() {
      setTimeout(() => {
        const messagesArea = this.$refs.messagesArea
        if (messagesArea) {
          messagesArea.scrollTop = messagesArea.scrollHeight
        }
      }, 100)
    },
    checkMobileDevice() {
      this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    },
    normalizeConfig(rawConfig) {
      const config = rawConfig || {}
      const welcomeMessages = config.welcomeMessages || {}
      const suggestedQuestions = Array.isArray(config.suggestedQuestions) ? config.suggestedQuestions : []

      return {
        idleImageUrl: config.idleImageUrl || '',
        openingVideoUrl: config.openingVideoUrl || '',
        replyVideoUrl: config.replyVideoUrl || '',
        welcomeMessages,
        suggestedQuestions: suggestedQuestions
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0)),
      }
    },
    initializeMessages() {
      this.messages = [
        createMessage(this.currentWelcomeMessage, 'received'),
      ]
    },
    updateWelcomeMessage() {
      if (this.messages.length > 0) {
        this.messages[0] = createMessage(this.currentWelcomeMessage, 'received')
      }
    },
    async buildResolvedMessage(content, type, extra = {}) {
      const convertImageLinks = type === 'received'

      return {
        content,
        type,
        segments: await resolveChatMessageSegments(content, { convertImageLinks }),
        ...extra,
      }
    },
    buildTtsTextFromSegments(segments) {
      return (segments || [])
        .filter(segment => segment.type !== 'image')
        .map(segment => segment.label || segment.content)
        .join('')
        .trim()
    },
    setVisualState(nextState, shouldPlay = false, withSound = false) {
      this.visualState = nextState
      this.videoRenderKey += 1

      if (shouldPlay && nextState.type === 'video') {
        this.$nextTick(() => {
          this.playVideo(withSound)
        })
      }
    },
    registerPendingVideoSoundRetry() {
      if (this.pendingVideoSoundRetry) {
        return
      }

      this.pendingVideoSoundRetry = true
      document.addEventListener('pointerdown', this.retryPendingVideoSoundPlayback)
      document.addEventListener('touchstart', this.retryPendingVideoSoundPlayback)
    },
    clearPendingVideoSoundRetry() {
      if (!this.pendingVideoSoundRetry) {
        return
      }

      this.pendingVideoSoundRetry = false
      document.removeEventListener('pointerdown', this.retryPendingVideoSoundPlayback)
      document.removeEventListener('touchstart', this.retryPendingVideoSoundPlayback)
    },
    playVideo(withSound = false) {
      const video = this.$refs.videoPlayer
      if (!video) {
        return
      }

      video.currentTime = 0
      video.muted = true
      video.play().then(() => {
        if (!withSound) {
          this.clearPendingVideoSoundRetry()
          return null
        }

        video.muted = false
        video.currentTime = 0
        return video.play()
          .then(() => {
            this.clearPendingVideoSoundRetry()
          })
          .catch(() => {
            this.registerPendingVideoSoundRetry()
          })
      }).catch(() => {
        if (withSound) {
          this.registerPendingVideoSoundRetry()
        }
      })
    },
    retryPendingVideoSoundPlayback() {
      if (!this.pendingVideoSoundRetry) {
        return
      }

      const video = this.$refs.videoPlayer
      if (!video) {
        return
      }

      video.muted = false
      video.currentTime = 0
      video.play()
        .then(() => {
          this.clearPendingVideoSoundRetry()
        })
        .catch(() => {})
    },
    showIdleVisual() {
      this.currentVisualMode = 'idle'
      this.clearPendingVideoSoundRetry()

      if (this.siteConfig.idleImageUrl) {
        this.setVisualState({
          type: 'image',
          src: this.siteConfig.idleImageUrl,
          loop: false,
          muted: true,
        })
        return
      }

      this.setVisualState({
        type: 'video',
        src: this.siteConfig.openingVideoUrl || FALLBACK_OPENING_VIDEO,
        loop: false,
        muted: false,
      })
    },
    playOpeningVisual() {
      this.currentVisualMode = 'opening'
      this.setVisualState({
        type: 'video',
        src: this.siteConfig.openingVideoUrl || FALLBACK_OPENING_VIDEO,
        loop: false,
        muted: false,
      }, true, true)
    },
    playReplyVisual() {
      this.currentVisualMode = 'reply'
      this.setVisualState({
        type: 'video',
        src: this.siteConfig.replyVideoUrl || FALLBACK_REPLY_VIDEO,
        loop: true,
        muted: true,
      }, true, false)
    },
    handleVideoEnded() {
      if (this.currentVisualMode === 'opening') {
        this.showIdleVisual()
      }
    },
    toggleLanguageMenu() {
      this.showLanguageMenu = !this.showLanguageMenu
    },
    selectLanguage(lang) {
      this.selectedLanguage = lang
      this.showLanguageMenu = false
      this.updateWelcomeMessage()
    },
    async fetchSiteConfig() {
      const response = await api.getknowledge.getConfig()
      if (response.data.success && response.data.data) {
        this.siteConfig = this.normalizeConfig(response.data.data)
      }
    },
    parseParams(paramString, queryAgencyCode = null) {
      if (!paramString) {
        return {
          id: null,
          agencyCode: queryAgencyCode || null,
        }
      }

      const [pathPart, queryString = ''] = paramString.split('?')
      const ampersandIndex = pathPart.indexOf('&')
      const pathId = ampersandIndex === -1 ? pathPart : pathPart.substring(0, ampersandIndex)
      const legacyAgencyPart = ampersandIndex === -1 ? '' : pathPart.substring(ampersandIndex + 1)

      let agencyCode = queryAgencyCode || null

      if (!agencyCode && legacyAgencyPart.startsWith('agencyCode=')) {
        agencyCode = legacyAgencyPart.substring('agencyCode='.length)
      }

      if (!agencyCode && queryString) {
        const queryParams = new URLSearchParams(queryString)
        agencyCode = queryParams.get('agencyCode')
      }

      return {
        id: decodeURIComponent(pathId),
        agencyCode,
      }
    },
    async bootstrap() {
      this.checkMobileDevice()
      const rawParam = this.$route.params.param
      const queryAgencyCode =
        typeof this.$route.query.agencyCode === 'string' ? this.$route.query.agencyCode : null
      const { id, agencyCode } = this.parseParams(rawParam, queryAgencyCode)
      this.param = id || rawParam
      this.agencyCode = agencyCode

      try {
        const response = await api.getknowledge.getKnowledge(this.param)
        this.knowledgeData = response.data
        if (response.data.success && response.data.data) {
          localStorage.setItem('token', response.data.data)
          await this.fetchSiteConfig()
        }
      } catch (error) {
        console.error('初始化失敗:', handleApiError(error))
      } finally {
        this.initializeMessages()
        this.showIdleVisual()
        this.initialLoading = false
        this.$nextTick(() => {
          this.playOpeningVisual()
        })
      }
    },
    stopSpeaking() {
      speechService.stopSpeaking()
      this.isSpeaking = false
      this.showIdleVisual()
    },
    async handleTextToSpeech(text) {
      if (!(speechService && typeof speechService.textToSpeech === 'function')) {
        return
      }

      try {
        this.isSpeaking = true
        this.playReplyVisual()

        await speechService.textToSpeech(text, this.selectedLanguage, () => {
          this.isSpeaking = false
          this.showIdleVisual()
        })
      } catch (speechError) {
        console.error('文字轉語音失敗:', handleSpeechError(speechError))
        this.isSpeaking = false
        this.showIdleVisual()
      }
    },
    async sendMessage(presetQuestion = null) {
      if (!this.selectedLanguage || this.showLanguageMenu) {
        return
      }

      this.stopSpeaking()

      const normalizedPresetQuestion =
        typeof presetQuestion === 'string' ? presetQuestion : null
      const question = (normalizedPresetQuestion || this.newMessage).trim()
      if (!question || this.isLoading) {
        return
      }

      this.isLoading = true

      if (!this.guid) {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        this.guid = Array.from({ length: 8 }, () =>
          characters.charAt(Math.floor(Math.random() * characters.length))
        ).join('')
      }

      this.messages.push(createMessage(question, 'sent'))
      this.newMessage = ''

      const loadingMessageIndex = this.messages.length
      this.messages.push({
        content: '',
        type: 'received',
        isLoading: true,
        segments: [],
      })

      try {
        const response = await api.chat.sendMessage(this.guid, this.agencyCode, question)

        if (response.data.success) {
          const aiResponse = response.data.data.content
          if (response.data.data.guid) {
            this.guid = response.data.data.guid
          }

          const resolvedMessage = await this.buildResolvedMessage(aiResponse, 'received', {
            isLoading: false,
          })
          this.messages[loadingMessageIndex] = resolvedMessage

          this.scrollToBottom()
          const ttsText = this.buildTtsTextFromSegments(resolvedMessage.segments)
          if (ttsText) {
            this.handleTextToSpeech(ttsText)
          }
        }
      } catch (error) {
        console.error('發送消息失敗:', handleApiError(error))
        this.messages[loadingMessageIndex] = createMessage('抱歉，發送消息時出現錯誤', 'received')
        this.scrollToBottom()
      } finally {
        this.isLoading = false
      }
    },
    sendPresetQuestion(question) {
      if (!this.selectedLanguage || this.showLanguageMenu) {
        return
      }

      this.sendMessage(question)
    },
    async startVoiceInput() {
      if (this.isListening) return

      try {
        await navigator.mediaDevices.getUserMedia({ audio: true })
        this.isListening = true

        await speechService.speechToText(this.selectedLanguage, transcribedText => {
          if (transcribedText) {
            this.newMessage = transcribedText.trim()
          }
        })
      } catch (error) {
        console.error('語音識別錯誤:', error)
        this.isListening = false

        if (error.name === 'NotAllowedError' || error.message.includes('麥克風權限')) {
          alert('請允許使用麥克風權限以使用語音輸入功能')
        } else {
          alert('語音識別發生錯誤，請重試')
        }
      }
    },
    async stopVoiceInput() {
      if (!this.isListening) return

      try {
        await speechService.stopSpeechToText()
      } catch (error) {
        console.error('停止語音識別錯誤:', error)
      } finally {
        this.isListening = false
        if (this.newMessage.trim()) {
          this.sendMessage()
        }
      }
    },
  },
  async created() {
    await this.bootstrap()
  },
  updated() {
    this.scrollToBottom()
  },
  beforeUnmount() {
    this.clearPendingVideoSoundRetry()
    if (this.recognition) {
      this.recognition.stop()
    }
  },
}
</script>

<style scoped>
.page-container {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  padding-bottom: env(safe-area-inset-bottom);
  background: #000;
}

.video-area {
  height: 40vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-area:not([v-show="false"]) {
  opacity: 1;
}

.video-area video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.title-area {
  background-color: #666666;
  padding: 8px 16px;
}

.title-content {
  max-width: 414px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-content h2 {
  color: white;
  font-weight: bold;
  margin: 0;
  font-size: 1.1rem;
}

.language-button {
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 4px 8px;
  border: 1px solid white;
  border-radius: 4px;
}

.language-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.chat-container {
  height: 60vh;
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  background: #fff;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.messages-area {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  background: #f5f5f5;
  -webkit-overflow-scrolling: touch;
}

.message {
  margin: 8px 0;
  padding: 8px 12px;
  border-radius: 16px;
  max-width: 85%;
  word-break: break-word;
  clear: both;
}

.sent {
  background: gray;
  color: white;
  float: right;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 16px;
}

.received {
  background: white;
  color: #000;
  float: left;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.message-content {
  display: inline-block;
}

.message-text {
  white-space: pre-wrap;
}

.message-link {
  color: #1f6feb;
  text-decoration: underline;
  word-break: break-all;
}

.message-image-link {
  display: inline-block;
  text-decoration: none;
}

.message-image {
  display: block;
  max-width: 220px;
  width: 100%;
  border-radius: 12px;
  margin-top: 8px;
}

.faq-area {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 10px 0;
  background: white;
}

.input-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 10px 10px 0;
}

.faq-button {
  background: #ffd84d !important;
  color: #333 !important;
  border: none;
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 14px;
  min-width: auto;
}

.input-area {
  display: flex;
  gap: 8px;
  padding: 10px;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  box-sizing: border-box;
}

input {
  flex: 1;
  padding: 12px;
  border: none;
  font-size: 16px;
  background: white;
  outline: none;
}

button {
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 16px;
  min-width: 70px;
}

.language-dropdown {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  width: 200px;
  text-align: center;
  max-height: 300px;
  overflow-y: auto;
}

.language-option {
  padding: 12px 20px;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 1px solid #f5f5f5;
  font-size: 14px;
  color: #000000;
}

.language-option:last-child {
  border-bottom: none;
}

.language-option:hover {
  background: #f5f5f5;
}

@media (min-width: 600px) {
  .chat-container {
    max-width: 414px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }

  .video-area {
    display: flex;
    justify-content: center;
  }
}

.loading-dots::after {
  content: '';
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0%, 20% { content: ''; }
  40% { content: '.'; }
  60% { content: '..'; }
  80%, 100% { content: '...'; }
}

button:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #666666;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.send-button {
  padding: 8px;
  background: #666666;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.mic-button {
  background: #007bff !important;
}

.mic-button.listening {
  background-color: #ff4444 !important;
}

.mic-hint {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
}
</style>
